import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useTable, useSortBy, useGlobalFilter, usePagination } from 'react-table';
import { useHistory, useParams } from 'react-router-dom';
import { Spinner, Button, Alert } from 'react-bootstrap';
import { toast } from 'sonner';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { NoData } from '../../SharedComponents/NoData';
import PageHeader from '../../../layouts/PageHeader';
import { GlobalFilter } from '../../../components/AppsMenu/Quotes/GlobalFilter';
// Select removed — using filter chips instead

import VatPeriodStepper from './VatPeriodStepper';
import VatAdvisoryRail from './VatAdvisoryRail';
import { promptAndCloseHistoricalPeriod, hasPeriodEnded } from './closeHistoricalPeriod';

import {
  Calendar,
  AlertTriangle,
  CheckCircle2,
  Clock,
  CreditCard,
  Settings,
  FileText,
  RefreshCw,
  Loader2,
  Lock, Receipt
} from 'lucide-react';

function VatReportingPeriods() {
  const { companyId } = useParams();
  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [historicalOpenCount, setHistoricalOpenCount] = useState(0);
  const [closingHistorical, setClosingHistorical] = useState(false);
  const history = useHistory();

  // Use sterpper to handle vat period
  const [useStepper, setUseStepper] = useState(true);


  const [allPeriods, setAllPeriods] = useState([]); // All periods across all fiscal years
  const [filterYear, setFilterYear] = useState(null); // null = show all

  const fetchVatPeriods = useCallback(async (fiscalYearId) => {
    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/reporting/vat-reporting-periods`),
        {
          params: { fiscalYearId },
          withCredentials: true
        }
      );
      if (response.data.success) {
        setPeriods(response.data.periods);
      } else {
        setError('Failed to load VAT reporting periods');
      }
    } catch (err) {
      setError('Error fetching VAT reporting periods');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [companyId]);

  // Fetch ALL periods across all fiscal years
  const fetchAllPeriods = useCallback(async (years) => {
    try {
      const allResults = [];
      for (const fy of years) {
        const response = await axios.get(
          getApiUrl(`${companyId}/reporting/vat-reporting-periods`),
          { params: { fiscalYearId: fy.fiscal_year_id }, withCredentials: true }
        );
        if (response.data.success) {
          response.data.periods.forEach(p => {
            allResults.push({ ...p, fiscal_year_id: fy.fiscal_year_id, fiscal_year: fy.year });
          });
        }
      }
      // Count totals per fiscal year BEFORE filtering (for "Period X av Y" display)
      const totalsPerFY = {};
      allResults.forEach(p => {
        const fy = p.fiscal_year_id;
        totalsPerFY[fy] = (totalsPerFY[fy] || 0) + 1;
      });

      // Filter out future periods (start_date hasn't arrived yet)
      const today = new Date().toISOString().split('T')[0];
      const visible = allResults.filter(p => p.start_date <= today);
      // Attach the total count to each visible period
      visible.forEach(p => { p._fyTotal = totalsPerFY[p.fiscal_year_id] || 0; });
      // Sort by start_date descending (newest first)
      visible.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
      setAllPeriods(visible);
      setPeriods(visible);
    } catch (err) {
      setError('Error fetching VAT reporting periods');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const handleRefresh = () => {
    if (selectedFiscalYear) {
      setRefreshing(true);
      fetchVatPeriods(selectedFiscalYear.fiscal_year_id);
    }
  };

  useEffect(() => {
    const getFiscalYears = async () => {
      try {
        const result = await axios.get(getApiUrl(`${companyId}/accounting/fiscalyears`), { withCredentials: true });
        const years = result.data.fiscalYears || [];
        setFiscalYears(years);
        if (years.length > 0) {
          const sorted = [...years].sort((a, b) => b.year - a.year);
          setSelectedFiscalYear(sorted[0]);
          await fetchAllPeriods(years);
        }
      } catch (err) {
        console.error('Error fetching fiscal years:', err);
        setFiscalYears([]);
        setError('Det gick inte att hämta räkenskapsår. Försök igen senare.');
      } finally {
        setLoading(false);
      }
    };

    getFiscalYears();
  }, [companyId, fetchAllPeriods]);

  // Check for open historical periods across all fiscal years EXCEPT the latest
  const checkHistoricalPeriods = useCallback(async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const sortedYears = [...fiscalYears].sort((a, b) => b.year - a.year);
      const latestFYId = sortedYears.length > 0 ? sortedYears[0].fiscal_year_id : null;

      let totalOpen = 0;
      for (const fy of fiscalYears) {
        if (fy.fiscal_year_id === latestFYId) continue; // Skip current year
        const res = await axios.get(getApiUrl(`${companyId}/reporting/vat-reporting-periods`), {
          params: { fiscalYearId: fy.fiscal_year_id }, withCredentials: true
        });
        if (res.data.success) {
          totalOpen += res.data.periods.filter(p => p.state === 'open').length;
        }
      }
      setHistoricalOpenCount(totalOpen);
    } catch (err) {
      // Non-critical
    }
  }, [companyId, fiscalYears]);

  useEffect(() => {
    if (fiscalYears.length > 0) {
      checkHistoricalPeriods();
    }
  }, [fiscalYears, checkHistoricalPeriods]);

  const handleCloseHistorical = async () => {
    setClosingHistorical(true);
    try {
      const res = await axios.post(
        getApiUrl(`${companyId}/reporting/vat-reporting-periods/close-historical`),
        {},
        { withCredentials: true }
      );
      if (res.data.success) {
        toast.success(res.data.message);
        setHistoricalOpenCount(0);
        await fetchAllPeriods(fiscalYears);
      }
    } catch (err) {
      toast.error('Kunde inte stänga historiska perioder');
    } finally {
      setClosingHistorical(false);
    }
  };

  const handleFiscalYearChange = (selectedOption) => {
    if (selectedOption) {
      setSelectedFiscalYear(selectedOption.value);
      setFilterYear(selectedOption.value.fiscal_year_id);
      setPeriods(allPeriods.filter(p => p.fiscal_year_id === selectedOption.value.fiscal_year_id));
    }
  };

  const handleShowAllYears = () => {
    setFilterYear(null);
    setSelectedFiscalYear(null);
    setPeriods(allPeriods);
  };

  // const handleRowClick = (periodId) => {
  //   history.push(`/${companyId}/vat-period/${periodId}`);
  // };

  const handleMarkAsHandled = async (period) => {
    const result = await promptAndCloseHistoricalPeriod({
      companyId,
      period,
      fiscalYearId: period.fiscal_year_id || selectedFiscalYear?.fiscal_year_id,
    });
    if (result.error) {
      toast.error(result.error);
      return;
    }
    if (!result.closed) return;
    toast.success(`Momsperiod ${period.start_date} – ${period.end_date} stängd`);
    await fetchAllPeriods(fiscalYears);
  };

  const handleRowClick = (period) => {
    history.push(`/${companyId}/vat-periods/${period.vat_reporting_period_id}`);
  };

  // The deadline's provenance, for the column tooltip.
  //
  // R-DL-8 (lag 1930:173 2 §) moves a deadline that falls on a lördag, söndag,
  // allmän helgdag, midsommarafton, julafton or nyårsafton to the next vardag,
  // so the stored date and the statutory date legitimately differ. Saying which
  // is which is what lets a user reconcile our date against Skatteverket's.
  const describeDueDate = (period) => {
    const parts = [];
    if (period.due_date_statutory && period.due_date_statutory !== period.due_date) {
      parts.push(
        `Lagstadgat datum ${period.due_date_statutory}, framflyttat till nästa vardag `
        + '(lag 1930:173 2 §)'
      );
    }
    return parts.length ? parts.join(' — ') : 'Deklarations- och betalningsdag';
  };

  // Helper function to check if a period is overdue
  const isOverdue = (period) => {
    const today = new Date();
    const dueDate = new Date(period.due_date);
    return period.state === 'open' && dueDate < today;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK'
    }).format(amount);
  };

  // Check if a period can be handled based on:
  // 1. Period end_date must have passed
  // 2. The chronologically previous period must be closed
  const getPeriodLockStatus = useCallback((period, allPeriods) => {
    if (period.state === 'closed') {
      return { locked: false, reason: null };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = new Date(period.end_date);
    endDate.setHours(0, 0, 0, 0);

    if (today <= endDate) {
      return {
        locked: true,
        reason: `Perioden har inte avslutats än (slutar ${new Date(period.end_date).toLocaleDateString('sv-SE')})`
      };
    }

    // Find the previous period across ALL fiscal years (not just filtered view)
    const sortedPeriods = [...allPeriods].sort(
      (a, b) => new Date(a.end_date) - new Date(b.end_date)
    );
    const thisIndex = sortedPeriods.findIndex(
      p => p.vat_reporting_period_id === period.vat_reporting_period_id
    );
    const previousPeriod = thisIndex > 0 ? sortedPeriods[thisIndex - 1] : null;

    if (previousPeriod && previousPeriod.state !== 'closed') {
      return {
        locked: true,
        reason: `Föregående period (${new Date(previousPeriod.start_date).toLocaleDateString('sv-SE')} – ${new Date(previousPeriod.end_date).toLocaleDateString('sv-SE')}) måste stängas först`
      };
    }

    return { locked: false, reason: null };
  }, []);

  // Map each period to its chronological number (1-based, sorted by start_date ASC)
  const periodNumbers = useMemo(() => {
    // Group by fiscal year, number within each year
    const byYear = {};
    periods.forEach(p => {
      const fy = p.fiscal_year_id || 'unknown';
      if (!byYear[fy]) byYear[fy] = [];
      byYear[fy].push(p);
    });
    const map = {};
    Object.values(byYear).forEach(yearPeriods => {
      yearPeriods.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
      yearPeriods.forEach((p, i) => {
        // Use _fyTotal (from unfiltered count) for the total, not the filtered count
        map[p.vat_reporting_period_id] = { num: i + 1, total: p._fyTotal || yearPeriods.length };
      });
    });
    return map;
  }, [periods]);

  const COLUMNS = useMemo(() => [
    {
      Header: 'År',
      accessor: 'fiscal_year',
      Cell: ({ value }) => (
        <span style={{ fontSize: '0.8rem', fontWeight: 500, color: '#475569' }}>
          {value || '—'}
        </span>
      ),
    },
    {
      Header: 'Period',
      accessor: 'start_date',
      Cell: ({ value, row }) => (
        <div>
          <div style={{ fontWeight: 500, fontSize: '0.82rem', color: '#1e293b' }}>
            {new Date(value).toLocaleDateString('sv-SE')} – {new Date(row.original.end_date).toLocaleDateString('sv-SE')}
          </div>
          <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
            Period {periodNumbers[row.original.vat_reporting_period_id]?.num} av {periodNumbers[row.original.vat_reporting_period_id]?.total}
          </div>
        </div>
      ),
    },
    {
      Header: 'Moms att betala/få tillbaka',
      accessor: 'vat_total',
      Cell: ({ value }) => {
        if (value === null || value === undefined || Number(value) === 0) return <span style={{ color: '#94a3b8' }}>—</span>;
        return (
          <span style={{
            fontSize: '0.875rem',
            fontWeight: '600',
            color: value > 0 ? 'var(--bs-danger)' : 'var(--primary)'
          }}>
            {formatCurrency(value)}
          </span>
        );
      },
    },
    {
      Header: 'Deklaration senast',
      accessor: 'due_date',
      Cell: ({ value, row }) => (
        <div>
          <span
            style={{
              fontSize: '0.875rem',
              fontWeight: isOverdue(row.original) ? '600' : '500',
              color: isOverdue(row.original) ? 'var(--bs-danger)' : '#374151'
            }}
            // Which rule produced this date, and whether the helgdagsregel
            // moved it. A deadline nobody can trace is a deadline nobody can
            // check — see docs/vat-compliance/VAT_REGULATORY_BASELINE.md.
            title={describeDueDate(row.original)}
          >
            {new Date(value).toLocaleDateString('sv-SE')}
          </span>
          {isOverdue(row.original) && (
            <div style={{ fontSize: '0.75rem', color: 'var(--bs-danger)', fontWeight: '500' }}>
              FÖRSENAD!
            </div>
          )}
          {row.original.due_date_statutory
            && row.original.due_date_statutory !== row.original.due_date && (
            <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
              flyttad från helgdag
            </div>
          )}
        </div>
      ),
    },
    {
      Header: 'Steg',
      accessor: 'current_step',
      Cell: ({ value, row }) => {
        if (row.original.state === 'closed') return <span className="text-muted" style={{ fontSize: '0.75rem' }}>—</span>;
        const steps = ['instructions', 'review', 'submission', 'payment', 'record'];
        const stepNum = steps.indexOf(value) + 1;
        const stepLabels = {
          'instructions': 'Instruktioner',
          'review': 'Granskning',
          'submission': 'Inlämning',
          'payment': 'Betalning',
          'record': 'Bokföring'
        };
        return (
          <span style={{ fontSize: '0.75rem', color: 'var(--secondary)' }}>
            {stepNum}/5 — {stepLabels[value] || value}
          </span>
        );
      },
    },
    {
      Header: 'Status',
      accessor: 'state',
      Cell: ({ value, row }) => {
        const getStatusConfig = (status, isOverdueStatus) => {
          if (isOverdueStatus) {
            return { bg: '#fee2e2', color: '#991b1b', text: 'Försenad' };
          }

          switch (status) {
            case 'open':
              return { bg: '#fef3c7', color: '#92400e', text: 'Öppen' };
            case 'closed':
              return { bg: 'var(--rgba-primary-1)', color: '#065f46', text: 'Stängd' };
            case 'submitted':
              return { bg: 'var(--rgba-primary-2)', color: '#1e40af', text: 'Inlämnad' };
            default:
              return { bg: '#f3f4f6', color: '#374151', text: status };
          }
        };

        const config = getStatusConfig(value, isOverdue(row.original));

        return (
          <span
            style={{
              fontSize: '0.75rem',
              fontWeight: '500',
              padding: '0.25rem 0.75rem',
              borderRadius: '1rem',
              backgroundColor: config.bg,
              color: config.color,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.25rem'
            }}
          >
            {isOverdue(row.original) && <AlertTriangle size={12} />}
            {value === 'open' && !isOverdue(row.original) && <Clock size={12} />}
            {value === 'closed' && <CheckCircle2 size={12} />}
            {value === 'submitted' && <CheckCircle2 size={12} />}
            {config.text}
          </span>
        );
      },
    },
    {
      Header: '',
      id: 'actions',
      disableSortBy: true,
      Cell: ({ row }) => {
        const period = row.original;
        if (period.state === 'closed') return null;
        // A period still running cannot have been declared; the server refuses
        // it with VAT_PERIOD_NOT_ELAPSED, so offering the action is only a way
        // to produce an error.
        if (!hasPeriodEnded(period)) return null;
        return (
          <Button
            variant="link"
            size="sm"
            className="p-0 text-muted"
            style={{ fontSize: '0.75rem' }}
            onClick={(e) => {
              e.stopPropagation();
              handleMarkAsHandled(period);
            }}
          >
            Stäng period
          </Button>
        );
      },
    }
  ], [periods, periodNumbers]);

  const tableInstance = useTable(
    {
      columns: COLUMNS,
      data: periods,
      initialState: { pageSize },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    gotoPage,
    pageCount,
    setPageSize: setTablePageSize,
    setGlobalFilter,
  } = tableInstance;

  const { globalFilter, pageIndex } = state;

  // Enhanced pagination controls
  const PaginationControls = () => (
    <div
      style={{
        backgroundColor: '#fff',
        borderTop: '1px solid #f1f5f9',
        padding: '1rem 1.5rem'
      }}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <select
            className="form-select form-select-sm"
            style={{
              width: 'auto',
              fontSize: '0.875rem',
              border: '1px solid #e5e7eb',
              borderRadius: '6px'
            }}
            value={pageSize}
            onChange={e => {
              setPageSize(Number(e.target.value));
              setTablePageSize(Number(e.target.value));
            }}
          >
            {[10, 20, 30, 40, 50].map(size => (
              <option key={size} value={size}>{size} per sida</option>
            ))}
          </select>
          <span style={{ fontSize: '0.875rem', color: 'var(--secondary)' }}>
            Visar {page.length} av {periods.length} momsperioder
          </span>
        </div>

        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage}
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          >
            {'<<'}
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
            style={{ fontSize: '0.75rem' }}
          >
            Föregående
          </Button>
          <span style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>
            Sida {pageIndex + 1} av {pageOptions.length}
          </span>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => nextPage()}
            disabled={!canNextPage}
            style={{ fontSize: '0.75rem' }}
          >
            Nästa
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          >
            {'>>'}
          </Button>
        </div>
      </div>
    </div>
  );

  if (loading && !refreshing) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Laddar...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .table td {
          vertical-align: middle !important;
        }
        
        .clickable-row {
          cursor: pointer;
        }
        
        .clickable-row:hover {
          background-color: #f8f9fa;
        }
        
        .btn-outline-secondary {
          border-color: #6c757d !important;
        }
        
        .btn-outline-secondary:hover {
          background-color: #6c757d !important;
          border-color: #6c757d !important;
          color: #fff !important;
        }
        
        /* Add a refresh button animation */
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>

      <PageHeader
        icon={Receipt}
        title="Momsrapport"
        subtitle="Momsperioder att granska, rapportera och bokföra."
      />

      <div className="col-12">
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        {/* Rådgivande rail (G10 momsbefrielse, G13 satsändring). Read once per
            page load — never per transaction — and never blocking. */}
        <VatAdvisoryRail companyId={companyId} />

        {fiscalYears.length === 0 && !loading ? (
          <>
            <NoData message="Det finns inga räkenskapsår att visa. Skapa ett nytt räkenskapsår för att börja lägga till verifikationer." />
            <div className="d-flex justify-content-center p-4">
              <Button variant="primary" onClick={() => history.push(`/${companyId}/settings/fiscal-years`)}>
                <Settings size={16} className="me-2" />
                Gå till Inställningar
              </Button>
            </div>
          </>
        ) : (
          <>
            {refreshing && (
              <div className="text-center mb-3">
                <Loader2 size={16} className="me-1 spin" />
                Uppdaterar momsperioder...
              </div>
            )}

            {/* The year filter sits OUTSIDE the empty-state branch on purpose.
                It used to live inside the table card, so filtering to a year
                with no periods replaced the whole card — filter included — with
                the empty message, and there was no control left to pick another
                year. A filter that removes itself is a dead end. */}
            <div className="card shadow-sm border-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
              <div className="card-body p-0">
                {/* Year filter */}
                <div className="p-3 border-bottom d-flex align-items-center gap-1 flex-wrap" style={{ backgroundColor: '#fafbfc' }}>
                    <button
                      onClick={handleShowAllYears}
                      className="btn btn-sm"
                      style={{
                        backgroundColor: filterYear === null ? 'var(--primary)' : '#f1f5f9',
                        color: filterYear === null ? 'white' : '#475569',
                        border: 'none',
                        borderRadius: '16px',
                        padding: '0.25rem 0.625rem',
                        fontSize: '0.75rem',
                        fontWeight: 500
                      }}
                    >
                      Alla
                    </button>
                    {[...fiscalYears].sort((a, b) => b.year - a.year).map(fy => (
                      <button
                        key={fy.fiscal_year_id}
                        onClick={() => handleFiscalYearChange({ value: fy })}
                        className="btn btn-sm"
                        style={{
                          backgroundColor: filterYear === fy.fiscal_year_id ? 'var(--primary)' : '#f1f5f9',
                          color: filterYear === fy.fiscal_year_id ? 'white' : '#475569',
                          border: 'none',
                          borderRadius: '16px',
                          padding: '0.25rem 0.625rem',
                          fontSize: '0.75rem',
                          fontWeight: 500
                        }}
                      >
                        {fy.year}
                      </button>
                    ))}
                </div>

                {periods.length === 0 && !loading ? (
                  <div className="p-4">
                    <NoData message={filterYear
                      ? 'Det finns inga momsperioder för det valda räkenskapsåret. Välj ett annat år ovan.'
                      : 'Det finns inga momsperioder att visa.'} />
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table {...getTableProps()} className="table table-hover mb-0">
                        <thead style={{ backgroundColor: '#fff', borderBottom: '2px solid #f1f5f9' }}>
                          {headerGroups.map((headerGroup, headerGroupIndex) => (
                            <tr {...headerGroup.getHeaderGroupProps()} key={`header-group-${headerGroupIndex}`}>
                              {headerGroup.headers.map((column, columnIndex) => (
                                <th
                                  {...column.getHeaderProps(column.getSortByToggleProps())}
                                  key={`header-${columnIndex}-${column.id}`}
                                  className="fw-medium border-0 py-2 px-3"
                                  style={{
                                    cursor: column.canSort ? 'pointer' : 'default',
                                    userSelect: 'none',
                                    fontSize: '0.75rem',
                                    color: 'var(--secondary)',
                                    backgroundColor: '#f8fafc',
                                    borderBottom: '1px solid #e2e8f0'
                                  }}
                                >
                                  <div className="d-flex align-items-center">
                                    <span>{column.render('Header')}</span>
                                    {column.canSort && (
                                      <span className="ms-2">
                                        {column.isSorted ? (
                                          column.isSortedDesc ?
                                            <i className="fa fa-sort-down" style={{ fontSize: '0.75rem', color: 'var(--primary)' }} />
                                            :
                                            <i className="fa fa-sort-up" style={{ fontSize: '0.75rem', color: 'var(--primary)' }} />
                                        ) : (
                                          <i className="fa fa-sort" style={{ fontSize: '0.75rem', color: '#cbd5e1', opacity: '0.6' }} />
                                        )}
                                      </span>
                                    )}
                                  </div>
                                </th>
                              ))}
                            </tr>
                          ))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                          {page.map((row) => {
                            prepareRow(row);
                            return (
                              <React.Fragment key={row.id}>
                                <tr
                                  {...row.getRowProps()}
                                  onClick={() => handleRowClick(row.original)}
                                  style={{
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease',
                                    borderBottom: '1px solid #f1f5f9',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'var(--rgba-primary-1)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  {row.cells.map(cell => (
                                    <td
                                      {...cell.getCellProps()}
                                      key={cell.column.id}
                                      className="py-2 px-3 align-middle border-0"
                                      style={{
                                        fontSize: '0.82rem',
                                        color: '#374151'
                                      }}
                                    >
                                      {cell.column.id === 'actions' ? cell.render('Cell') : (cell.value !== null && cell.value !== undefined ? cell.render('Cell') : '-')}
                                    </td>
                                  ))}
                                </tr>
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {periods.length > pageSize && <PaginationControls />}
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default VatReportingPeriods;
